import type {
  ApiResponse,
  License,
  CreateLicenseData,
  LicenseValidationResult,
  TenantLimits,
  TenantLimitsCheck,
  UsageEvent,
  UsageSummary,
  TenantUsageMonthly,
  Invoice,
  TenantPlan,
  BillingSummary,
  Extension,
  ExtensionLicense,
  RegisterExtensionData,
  ExtensionVerificationResult,
  ExtensionInstall,
  Tenant,
} from './types'

// In production: empty string = relative URLs (same origin)
// In development: Vite proxy handles /api -> http://localhost:5050
const API_BASE_URL = ''

// ============================================================================
// API CLIENT CLASS
// ============================================================================
class ApiClient {
  private baseUrl: string
  private defaultHeaders: Record<string, string>

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    }
  }

  /**
   * Set authentication token
   */
  setAuthToken(token: string) {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`
  }

  /**
   * Clear authentication token
   */
  clearAuthToken() {
    delete this.defaultHeaders['Authorization']
  }

  /**
   * Make a fetch request with error handling
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.defaultHeaders,
          ...options.headers,
        },
      })

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type')
      if (!contentType?.includes('application/json')) {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
        return { success: true } as ApiResponse<T>
      }

      // Parse JSON response
      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.message || response.statusText,
          ...data, // Include detailed validation errors (e.g. fieldErrors)
        }
      }

      return data
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      }
    }
  }

  private get<T>(endpoint: string, options?: { query?: string }) {
    const url = options?.query ? `${endpoint}?${options.query}` : endpoint
    return this.request<T>(url, { method: 'GET' })
  }

  private post<T>(endpoint: string, body?: unknown) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  private put<T>(endpoint: string, body?: unknown) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  private patch<T>(endpoint: string, body?: unknown) {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  private delete<T>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }


  // ============================================================================
  // HEALTH CHECK
  // ============================================================================
  async healthCheck() {
    return this.get<{ status: string; timestamp: string }>('/api/health')
  }

  // ============================================================================
  // LICENSES
  // ============================================================================
  async listLicenses(params?: { plan?: string; valid?: boolean; page?: number; limit?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.plan) searchParams.set('plan', params.plan)
    if (params?.valid !== undefined) searchParams.set('valid', String(params.valid))
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.limit) searchParams.set('limit', String(params.limit))

    const query = searchParams.toString()
    return this.get<{ data: License[]; meta?: { page: number; limit: number; total?: number } }>(`/api/licenses${query ? `?${query}` : ''}`)
  }

  async getLicense(id: number) {
    return this.get<License>(`/api/license/${id}`)
  }

  async createLicense(data: CreateLicenseData) {
    return this.post<License>('/api/licenses', data)
  }

  async validateLicense(licenseKey: string) {
    return this.get<LicenseValidationResult>(`/api/licenses/${licenseKey}/validate`)
  }

  async updateLicenseExpiration(id: number, expiresAt: string) {
    return this.patch<License>(`/api/license/${id}/expiration`, { expiresAt })
  }

  async revokeLicense(id: number) {
    return this.post<License>(`/api/license/${id}/revoke`)
  }

  // ============================================================================
  // TENANTS
  // ============================================================================
  async listTenants(params?: { page?: number; limit?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', String(params.page))
    if (params?.limit) searchParams.set('limit', String(params.limit))

    const query = searchParams.toString()
    return this.get<{ data: Array<Tenant & { tenantPlan?: TenantPlan }>; meta: { page: number; limit: number; total?: number } }>(`/api/tenants${query ? `?${query}` : ''}`)
  }

  async getTenant(id: number) {
    return this.get<Tenant>(`/api/tenants/${id}`)
  }

  async createTenant(data: { name: string; domain?: string; licenseId?: number }) {
    return this.post<Tenant>('/api/tenants', data)
  }

  async updateTenant(id: number, data: { name?: string; domain?: string; licenseId?: number }) {
    return this.put<Tenant>(`/api/tenants/${id}`, data)
  }

  async suspendTenant(id: number) {
    return this.post<Tenant>(`/api/tenants/${id}/suspend`)
  }

  async activateTenant(id: number) {
    return this.post<Tenant>(`/api/tenants/${id}/activate`)
  }

  async deleteTenant(id: number) {
    return this.delete<Tenant>(`/api/tenants/${id}`)
  }

  async getTenantLimits(tenantId: number) {
    return this.get<TenantLimits>(`/api/tenants/${tenantId}/limits`)
  }

  async checkTenantLimits(tenantId: number) {
    return this.get<TenantLimitsCheck>(`/api/tenants/${tenantId}/check-limits`)
  }

  // ============================================================================
  // USAGE
  // ============================================================================
  async reportUsage(data: {
    tenantId: number
    resourceType: string
    action: string
    quantity?: number
    metadata?: Record<string, unknown>
  }) {
    return this.post('/api/usage/report', data)
  }

  async getUsageSummary(tenantId: number, month?: string) {
    const query = month ? `month=${month}` : ''
    return this.get<UsageSummary>(`/api/usage/tenants/${tenantId}/summary`, { query })
  }

  async getUsageEvents(
    tenantId: number,
    params?: {
      startDate?: string
      endDate?: string
      resourceType?: string
      action?: string
      limit?: number
      offset?: number
    }
  ) {
    const searchParams = new URLSearchParams()
    if (params?.startDate) searchParams.set('startDate', params.startDate)
    if (params?.endDate) searchParams.set('endDate', params.endDate)
    if (params?.resourceType) searchParams.set('resourceType', params.resourceType)
    if (params?.action) searchParams.set('action', params.action)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.get<UsageEvent[]>(`/api/usage/tenants/${tenantId}/events${query ? `?${query}` : ''}`)
  }

  async getUsageHistory(tenantId: number, months?: number) {
    const query = months ? `months=${months}` : ''
    return this.get<TenantUsageMonthly[]>(`/api/usage/tenants/${tenantId}/history`, { query })
  }

  async getAggregatedUsage(month?: string) {
    const query = month ? `month=${month}` : ''
    return this.get(`/api/usage/aggregated`, { query })
  }

  async cleanupUsage(olderThanMonths?: number) {
    return this.post<{ deletedCount: number }>('/api/usage/cleanup', {
      olderThanMonths,
    })
  }

  async recalculateUsage(tenantId: number, month: string) {
    return this.post(`/api/usage/recalculate/${tenantId}`, { month })
  }

  // ============================================================================
  // BILLING
  // ============================================================================
  async listAllInvoices(params?: { status?: string; tenantId?: number; month?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.tenantId) searchParams.set('tenantId', String(params.tenantId))
    if (params?.month) searchParams.set('month', params.month)

    const query = searchParams.toString()
    return this.get<Invoice[]>('/api/billing/invoices', { query })
  }

  async getInvoice(id: number) {
    return this.get<Invoice>(`/api/billing/invoices/${id}`)
  }

  async createInvoice(data: {
    tenantId: number
    month: string
    amountCents: number
    currency?: string
    dueDate?: string
  }) {
    return this.post<Invoice>('/api/billing/invoices', data)
  }

  async updateInvoiceStatus(id: number, status: string, paidAt?: string) {
    return this.patch<Invoice>(`/api/billing/invoices/${id}/status`, { status, paidAt })
  }

  async markInvoiceSent(id: number) {
    return this.post(`/api/billing/invoices/${id}/send`)
  }

  async markInvoicePaid(id: number, paidAt?: string) {
    return this.post(`/api/billing/invoices/${id}/pay`, { paidAt })
  }

  async generateInvoices(month: string) {
    return this.post('/api/billing/generate-invoices', { month })
  }

  async getOverdueInvoices() {
    return this.get<Invoice[]>('/api/billing/invoices/overdue')
  }

  async getTenantPlan(tenantId: number) {
    return this.get<TenantPlan>(`/api/billing/tenants/${tenantId}/plan`)
  }

  async upsertTenantPlan(
    tenantId: number,
    data: {
      planTier: string
      planLimits: Record<string, unknown>
      billingStatus?: string
      billingEmail?: string
    }
  ) {
    return this.put<TenantPlan>(`/api/billing/tenants/${tenantId}/plan`, data)
  }

  async getTenantBillingSummary(tenantId: number) {
    return this.get<BillingSummary>(`/api/billing/tenants/${tenantId}/summary`)
  }

  async getRevenueStats(month: string) {
    return this.get(`/api/billing/revenue-stats/${month}`)
  }

  async getRevenueShare(month: string) {
    return this.get(`/api/billing/revenue-share/${month}`)
  }

  async upsertRevenueShare(month: string, data: { status?: string; reportedAt?: string; paidAt?: string }) {
    return this.post(`/api/billing/revenue-share/${month}`, data)
  }

  async getRevenueShares(params?: { status?: string; month?: string; limit?: number; offset?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.month) searchParams.set('month', params.month)
    if (params?.limit) searchParams.set('limit', String(params.limit))
    if (params?.offset) searchParams.set('offset', String(params.offset))

    const query = searchParams.toString()
    return this.get(`/api/billing/revenue-shares`, { query })
  }

  // ============================================================================
  // EXTENSIONS
  // ============================================================================
  async listExtensions(params?: { category?: string; developerId?: string; status?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.category) searchParams.set('category', params.category)
    if (params?.developerId) searchParams.set('developerId', params.developerId)
    if (params?.status) searchParams.set('status', params.status)

    const query = searchParams.toString()
    return this.get<Extension[]>(`/api/extensions${query ? `?${query}` : ''}`)
  }

  async getExtension(id: string) {
    return this.get<Extension>(`/api/extensions/${id}`)
  }

  async createExtension(data: { name: string; version: string; description?: string; developerId: string; category?: string }) {
    return this.post<Extension>('/api/extensions', data)
  }

  async updateExtension(id: string, data: { name?: string; version?: string; description?: string; category?: string }) {
    return this.put<Extension>(`/api/extensions/${id}`, data)
  }

  async deleteExtension(id: string) {
    return this.delete<Extension>(`/api/extensions/${id}`)
  }

  async approveExtension(id: string, data: { status: 'approved' | 'rejected'; reviewNotes?: string }) {
    return this.patch<Extension>(`/api/extensions/${id}/review`, data)
  }

  async registerExtension(data: RegisterExtensionData) {
    return this.post<Extension>('/api/extensions/register', data)
  }

  async verifyExtension(extensionId: string, tenantId: number, licenseKey?: string) {
    return this.post<ExtensionVerificationResult>(`/api/extensions/${extensionId}/verify`, {
      tenantId,
      licenseKey,
    })
  }

  async reportExtensionUsage(extensionId: string, tenantId: number, metadata?: Record<string, unknown>) {
    return this.post(`/api/extensions/${extensionId}/usage`, { tenantId, metadata })
  }

  async getTenantExtensions(tenantId: number) {
    return this.get<ExtensionInstall[]>(`/api/extensions/tenant/${tenantId}`)
  }

  async updateExtensionStatus(id: string, status: string) {
    return this.patch<Extension>(`/api/extensions/${id}/status`, { status })
  }

  async generateExtensionLicense(
    extensionId: string,
    data: {
      tenantId?: number
      expiresAt?: string
      maxUsers?: number
      features?: Record<string, unknown>
    }
  ) {
    return this.post<ExtensionLicense>(`/api/extensions/${extensionId}/license`, data)
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL)

// Export class for custom instances if needed
export { ApiClient }

// Helper function to set auth token from stores
export function setAuthToken(token: string | null) {
  if (token) {
    api.setAuthToken(token)
  } else {
    api.clearAuthToken()
  }
}
